import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Tooltip,
  Chip,
  Divider,
} from "@mui/material";
import {
  ArrowLeft,
  Palette,
  Check,
  Building2,
  Globe,
  Save,
  RotateCcw,
  Sparkles,
  Wand2,
  ArrowLeftRight,
} from "lucide-react";
import { toast } from "react-toastify";
import axiosInstance from "../../axiosInstance";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import {
  isValidColor,
  parseColor,
  darkenColor,
  lightenColor,
  toRgbaString,
  generateGradient,
  getContrastTextColor,
  getGradientContrastTextColor,
} from "../../utils/colorUtils";
import "./EmployerSettings.css";

const PRESET_PALETTES = [
  { name: "Forest Green", color: "#0A6836" },
  { name: "Royal Indigo", color: "#4F46E5" },
  { name: "Emerald", color: "#059669" },
  { name: "Ocean Blue", color: "#2563EB" },
  { name: "Crimson Red", color: "#DC2626" },
  { name: "Sunset Amber", color: "#D97706" },
  { name: "Deep Violet", color: "#7C3AED" },
  { name: "Midnight Slate", color: "#1E293B" },
];

const PRESET_GRADIENTS = [
  { name: "Forest Glow", color1: "#0A6836", color2: "#22c55e" },
  { name: "Royal Indigo", color1: "#312e81", color2: "#3b82f6" },
  { name: "Emerald Mint", color1: "#065f46", color2: "#34d399" },
  { name: "Ocean Breeze", color1: "#1e3a8a", color2: "#06b6d4" },
  { name: "Sunset Flame", color1: "#9a3412", color2: "#f59e0b" },
  { name: "Crimson Rose", color1: "#991b1b", color2: "#fb7185" },
  { name: "Deep Violet", color1: "#581c87", color2: "#c084fc" },
  { name: "Midnight Steel", color1: "#0f172a", color2: "#64748b" },
];

export default function EmployerSettings() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { updateTheme } = useTheme();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [employer, setEmployer] = useState(null);

  // Active Theme Mode: "solid" (Single Color) or "gradient" (Gradient Color)
  const [themeType, setThemeType] = useState("solid");

  // Single Brand Color
  const [selectedColor, setSelectedColor] = useState("#0A6836");
  const [colorInput, setColorInput] = useState("#0A6836");
  const [colorError, setColorError] = useState("");

  // Gradient Color 1 (Start)
  const [gradient1, setGradient1] = useState("#0A6836");
  const [gradientInput1, setGradientInput1] = useState("#0A6836");
  const [gradientError1, setGradientError1] = useState("");

  // Gradient Color 2 (End)
  const [gradient2, setGradient2] = useState("#22c55e");
  const [gradientInput2, setGradientInput2] = useState("#22c55e");
  const [gradientError2, setGradientError2] = useState("");

  // Theme Text Color Setting (in config)
  const [textColor, setTextColor] = useState("#ffffff");
  const [textColorInput, setTextColorInput] = useState("#ffffff");
  const [textColorError, setTextColorError] = useState("");

  // Check role: Super Admin only
  useEffect(() => {
    if (user && user.roleId !== 1) {
      navigate("/list", { replace: true });
    }
  }, [user, navigate]);

  // Fetch employer details & current settings
  useEffect(() => {
    const fetchEmployerSettings = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await axiosInstance.get(`/employers/${id}/settings`);
        if (res.data?.success) {
          const emp = res.data.employer;
          const cfg = res.data.config || {};
          const type =
            res.data.themeType ||
            cfg.themeType ||
            (res.data.gradientColor1 && res.data.gradientColor2 ? "gradient" : "solid");
          const theme = res.data.colorTheme || emp?.colorTheme || "#0A6836";
          const g1 = res.data.gradientColor1 || cfg.gradientColor1 || darkenColor(theme, 15);
          const g2 = res.data.gradientColor2 || cfg.gradientColor2 || lightenColor(theme, 25);
          const txt = res.data.textColor || cfg.textColor || "#ffffff";

          setEmployer(emp);
          setThemeType(type);
          setSelectedColor(theme);
          setColorInput(theme);
          setGradient1(g1);
          setGradientInput1(g1);
          setGradient2(g2);
          setGradientInput2(g2);
          setTextColor(txt);
          setTextColorInput(txt);
        } else {
          setError("Failed to load employer settings");
        }
      } catch (err) {
        console.error("Error loading employer settings:", err);
        setError(err?.response?.data?.message || "Failed to load employer settings");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchEmployerSettings();
    }
  }, [id]);

  // Switch between Single Color and Gradient
  const handleThemeTypeChange = (type) => {
    setThemeType(type);
    if (type === "gradient") {
      if (!gradient1 || gradient1 === "#0A6836") {
        const base = selectedColor || "#0A6836";
        const g1 = darkenColor(base, 15);
        const g2 = lightenColor(base, 25);
        setGradient1(g1);
        setGradientInput1(g1);
        setGradient2(g2);
        setGradientInput2(g2);
      }
    } else {
      if (!colorInput || !isValidColor(colorInput)) {
        setColorInput(gradient1 || "#0A6836");
        setSelectedColor(gradient1 || "#0A6836");
      }
    }
  };

  // Sync single color from text input
  const handleColorInputChange = (val) => {
    setColorInput(val);
    if (isValidColor(val)) {
      const parsed = parseColor(val);
      setSelectedColor(parsed.hex || val);
      setColorError("");
    } else {
      setColorError("Please enter a valid Hex (e.g. #0A6836) or RGBA color");
    }
  };

  // Sync single color from native picker
  const handlePickerChange = (e) => {
    const hex = e.target.value;
    setSelectedColor(hex);
    setColorInput(hex);
    setColorError("");
  };

  // Select a preset single color
  const handleSelectPreset = (hex) => {
    setSelectedColor(hex);
    setColorInput(hex);
    setColorError("");
  };

  // Sync Gradient 1 from text input
  const handleGradientInput1Change = (val) => {
    setGradientInput1(val);
    if (isValidColor(val)) {
      const parsed = parseColor(val);
      setGradient1(parsed.hex || val);
      setGradientError1("");
    } else {
      setGradientError1("Invalid Hex or RGBA");
    }
  };

  // Sync Gradient 1 from native picker
  const handlePicker1Change = (e) => {
    const hex = e.target.value;
    setGradient1(hex);
    setGradientInput1(hex);
    setGradientError1("");
  };

  // Sync Gradient 2 from text input
  const handleGradientInput2Change = (val) => {
    setGradientInput2(val);
    if (isValidColor(val)) {
      const parsed = parseColor(val);
      setGradient2(parsed.hex || val);
      setGradientError2("");
    } else {
      setGradientError2("Invalid Hex or RGBA");
    }
  };

  // Sync Gradient 2 from native picker
  const handlePicker2Change = (e) => {
    const hex = e.target.value;
    setGradient2(hex);
    setGradientInput2(hex);
    setGradientError2("");
  };

  // Select a preset gradient pair
  const handleSelectGradientPreset = (c1, c2) => {
    setGradient1(c1);
    setGradientInput1(c1);
    setGradientError1("");
    setGradient2(c2);
    setGradientInput2(c2);
    setGradientError2("");
  };

  // Swap Gradient 1 and Gradient 2
  const handleSwapGradient = () => {
    const temp1 = gradient1;
    const tempInput1 = gradientInput1;
    setGradient1(gradient2);
    setGradientInput1(gradientInput2);
    setGradient2(temp1);
    setGradientInput2(tempInput1);
    setGradientError1("");
    setGradientError2("");
  };

  // Harmonize: auto-generate Gradient 2 from Gradient 1
  const handleAutoGenerateGradient = () => {
    const base = gradient1 || selectedColor || "#0A6836";
    const g2 = lightenColor(base, 25);
    setGradient2(g2);
    setGradientInput2(g2);
    setGradientError2("");
  };

  // Reset to default
  const handleReset = () => {
    if (themeType === "solid") {
      setSelectedColor("#0A6836");
      setColorInput("#0A6836");
      setColorError("");
    } else {
      setGradient1("#0A6836");
      setGradientInput1("#0A6836");
      setGradientError1("");
      setGradient2("#22c55e");
      setGradientInput2("#22c55e");
      setGradientError2("");
    }
    setTextColor("#ffffff");
    setTextColorInput("#ffffff");
    setTextColorError("");
  };

  // Save Theme Settings
  const handleSave = async () => {
    if (themeType === "solid") {
      if (!isValidColor(colorInput)) {
        setColorError("Please enter a valid Hex or RGBA color before saving");
        return;
      }
    } else {
      if (!isValidColor(gradientInput1)) {
        setGradientError1("Please enter a valid color for Gradient 1");
        return;
      }
      if (!isValidColor(gradientInput2)) {
        setGradientError2("Please enter a valid color for Gradient 2");
        return;
      }
    }
    if (textColorInput && !isValidColor(textColorInput)) {
      setTextColorError("Please enter a valid Hex or RGBA text color before saving");
      return;
    }

    try {
      setSaving(true);
      const cleanColor = colorInput.trim();
      const cleanGrad1 = gradientInput1.trim();
      const cleanGrad2 = gradientInput2.trim();
      const cleanTextColor = textColorInput ? textColorInput.trim() : "#ffffff";

      const effectiveThemeColor = themeType === "solid" ? cleanColor : cleanGrad1;

      const payload = {
        themeType,
        colorTheme: effectiveThemeColor,
        gradientColor1: themeType === "gradient" ? cleanGrad1 : null,
        gradientColor2: themeType === "gradient" ? cleanGrad2 : null,
        textColor: cleanTextColor,
        config: {
          themeType,
          themeColor: effectiveThemeColor,
          gradientColor1: themeType === "gradient" ? cleanGrad1 : null,
          gradientColor2: themeType === "gradient" ? cleanGrad2 : null,
          textColor: cleanTextColor,
        },
      };

      const res = await axiosInstance.put(`/employers/${id}/settings`, payload);

      if (res.data?.success) {
        toast.success(
          themeType === "solid"
            ? "Single color theme saved successfully!"
            : "Gradient theme saved successfully!"
        );
        setEmployer(res.data.employer);
        setThemeType(res.data.themeType || themeType);
        setSelectedColor(res.data.colorTheme);
        setColorInput(res.data.colorTheme);
        if (res.data.gradientColor1) {
          setGradient1(res.data.gradientColor1);
          setGradientInput1(res.data.gradientColor1);
        }
        if (res.data.gradientColor2) {
          setGradient2(res.data.gradientColor2);
          setGradientInput2(res.data.gradientColor2);
        }
        if (res.data.textColor) {
          setTextColor(res.data.textColor);
          setTextColorInput(res.data.textColor);
        }

        // Always update live theme in ThemeContext immediately
        updateTheme(res.data.colorTheme, res.data.config, res.data.employer);

        // Update stored user in localStorage if matching
        try {
          const storedUserStr = localStorage.getItem("user");
          if (storedUserStr) {
            const parsedUser = JSON.parse(storedUserStr);
            if (
              parsedUser.userId === res.data.employer?.userId ||
              parsedUser.employerId === res.data.employer?._id ||
              parsedUser.prefix?.toUpperCase() === res.data.employer?.prefix?.toUpperCase()
            ) {
              parsedUser.colorTheme = res.data.colorTheme;
              parsedUser.config = res.data.config;
              localStorage.setItem("user", JSON.stringify(parsedUser));
            }
          }
        } catch (e) {
          console.warn("Could not sync localStorage on save:", e);
        }
      } else {
        toast.error(res.data?.message || "Failed to update settings");
      }
    } catch (err) {
      console.error("Error saving employer settings:", err);
      toast.error(err?.response?.data?.message || "Failed to save employer settings");
    } finally {
      setSaving(false);
    }
  };

  // Derived preview variations
  const parsed = parseColor(selectedColor) || { hex: "#0A6836" };
  const currentHex = parsed.hex;

  const parsedG1 = parseColor(gradient1) || { hex: currentHex };
  const parsedG2 = parseColor(gradient2) || { hex: lightenColor(currentHex, 25) };

  const isGradientMode = themeType === "gradient";
  const activePreviewColor = isGradientMode ? parsedG1.hex : currentHex;
  const activePreviewGradient = isGradientMode
    ? generateGradient(parsedG1.hex, parsedG2.hex)
    : `linear-gradient(135deg, ${darkenColor(currentHex, 12)} 0%, ${currentHex} 100%)`;

  const hoverColor = darkenColor(activePreviewColor, 15);
  const lightColor = toRgbaString(activePreviewColor, 0.12);

  // Prioritize configured textColor; fallback to automatic luminance calculation
  const autoContrast = isGradientMode
    ? getGradientContrastTextColor(parsedG1.hex, parsedG2.hex)
    : getContrastTextColor(currentHex);

  const effectiveTextColor =
    textColor && isValidColor(textColor) ? textColor : autoContrast;

  const contrastText = effectiveTextColor;
  const contrastSubText = toRgbaString(contrastText, 0.85);
  const contrastOverlay = toRgbaString(contrastText, 0.18);

  if (loading) {
    return (
      <Box className="settings-loading-box">
        <CircularProgress size={44} style={{ color: "#4f46e5" }} />
        <Typography className="settings-loading-text">Loading employer settings...</Typography>
      </Box>
    );
  }

  return (
    <Box className="employer-settings-root">
      {/* Top Navigation Bar */}
      <Box className="settings-top-bar">
        <Button
          variant="text"
          className="back-to-employers-btn"
          startIcon={<ArrowLeft size={18} />}
          onClick={() => navigate("/employers")}
        >
          Back to Employers
        </Button>
      </Box>

      {/* Main Container */}
      <Box className="settings-main-container">
        {error && (
          <Alert severity="error" className="settings-alert-banner">
            {error}
          </Alert>
        )}

        {/* Employer Overview Header Card */}
        <Paper className="employer-info-card" elevation={1}>
          <Box className="employer-info-content">
            <Box className="employer-avatar-wrap" style={{ backgroundColor: lightColor, color: activePreviewColor }}>
              <Building2 size={28} />
            </Box>
            <Box className="employer-text-group">
              <Box className="employer-title-row">
                <Typography variant="h5" className="employer-business-name">
                  {employer?.employerName || "Employer Settings"}
                </Typography>
                <Chip
                  label={employer?.prefix ? `${employer.prefix}.domain` : "No Subdomain"}
                  icon={<Globe size={14} />}
                  className="subdomain-chip"
                  size="small"
                />
              </Box>
              <Typography className="employer-meta-sub">
                Proprietor: <strong>{employer?.fullName}</strong> &bull; User ID: <strong>{employer?.userId}</strong> &bull; Mobile: <strong>+91 {employer?.mobile}</strong>
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Theme Customization Grid */}
        <Box className="settings-grid-layout">
          {/* Left Column: Color & Gradient Configuration */}
          <Paper className="settings-panel color-config-panel" elevation={1}>
            <Box className="panel-header">
              <Box className="panel-icon-wrap">
                <Palette size={20} color="#4f46e5" />
              </Box>
              <Box>
                <Typography variant="h6" className="panel-title-text">
                  Theme Configuration
                </Typography>
                <Typography className="panel-subtitle-text">
                  Select either a Single Color or Gradient to apply as the employer's theme.
                </Typography>
              </Box>
            </Box>

            <Divider className="panel-divider" />

            {/* Theme Style Toggle (Single Color vs Gradient) */}
            <Box className="theme-type-selector-wrapper">
              <Typography className="field-section-label">Select Theme Style</Typography>
              <Box className="theme-type-selector">
                <button
                  type="button"
                  className={`theme-type-btn ${themeType === "solid" ? "theme-type-btn-active" : ""}`}
                  onClick={() => handleThemeTypeChange("solid")}
                >
                  <Palette size={18} />
                  <span>Single Color</span>
                </button>
                <button
                  type="button"
                  className={`theme-type-btn ${themeType === "gradient" ? "theme-type-btn-active" : ""}`}
                  onClick={() => handleThemeTypeChange("gradient")}
                >
                  <Sparkles size={18} />
                  <span>Gradient</span>
                </button>
              </Box>
            </Box>

            {/* ===================== MODE 1: SINGLE COLOR ===================== */}
            {themeType === "solid" && (
              <Box className="single-color-section">
                <Box className="color-picker-input-group">
                  <Typography className="field-section-label">Brand Color</Typography>
                  <Box className="picker-row">
                    {/* Visual Native Picker Box */}
                    <Tooltip title="Click to open color picker">
                      <Box className="color-swatch-box" style={{ backgroundColor: currentHex }}>
                        <input
                          type="color"
                          className="hidden-color-input"
                          value={currentHex.startsWith("#") && currentHex.length === 7 ? currentHex : "#0A6836"}
                          onChange={handlePickerChange}
                        />
                      </Box>
                    </Tooltip>

                    {/* Text Field for Hex or RGBA */}
                    <Box className="color-text-field-wrap">
                      <TextField
                        label="Hex ID or RGBA"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={colorInput}
                        onChange={(e) => handleColorInputChange(e.target.value)}
                        placeholder="#0A6836 or rgba(10, 104, 54, 1)"
                        error={!!colorError}
                        helperText={colorError || "Applies this single solid color across the entire application."}
                        InputProps={{
                          endAdornment: isValidColor(colorInput) ? (
                            <Check size={18} color="#10b981" />
                          ) : null,
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Quick Preset Color Swatches */}
                <Box className="preset-palettes-group">
                  <Typography className="field-section-label">Popular Brand Presets</Typography>
                  <Box className="preset-swatches-grid">
                    {PRESET_PALETTES.map((palette) => {
                      const isActive = currentHex.toLowerCase() === palette.color.toLowerCase();
                      return (
                        <Box
                          key={palette.color}
                          className={`preset-swatch-item ${isActive ? "preset-active" : ""}`}
                          onClick={() => handleSelectPreset(palette.color)}
                        >
                          <Box className="preset-swatch-circle" style={{ backgroundColor: palette.color }}>
                            {isActive && <Check size={14} color={getContrastTextColor(palette.color)} />}
                          </Box>
                          <Typography className="preset-swatch-name">{palette.name}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            )}

            {/* ===================== MODE 2: GRADIENT ===================== */}
            {themeType === "gradient" && (
              <Box className="gradient-customizer-section">
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography className="field-section-label" sx={{ mb: "0 !important" }}>
                    Gradient Setup (Start &rarr; End)
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      className="gradient-action-btn"
                      startIcon={<ArrowLeftRight size={13} />}
                      onClick={handleSwapGradient}
                    >
                      Swap
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      className="gradient-action-btn"
                      startIcon={<Wand2 size={13} />}
                      onClick={handleAutoGenerateGradient}
                    >
                      Harmonize
                    </Button>
                  </Box>
                </Box>

                {/* Active Gradient Strip */}
                <Box className="gradient-bar-wrapper">
                  <Box className="gradient-preview-strip" style={{ background: activePreviewGradient, color: contrastText }}>
                    <span>Start: {parsedG1.hex}</span>
                    <span className="gradient-tag" style={{ background: contrastOverlay, color: contrastText }}>135° Gradient</span>
                    <span>End: {parsedG2.hex}</span>
                  </Box>
                </Box>

                {/* Gradient 1 and Gradient 2 Side by Side Pickers */}
                <Box className="gradient-inputs-grid">
                  {/* Gradient 1: Start Color */}
                  <Box className="gradient-col-card">
                    <Typography className="gradient-col-title">Gradient 1 (Start)</Typography>
                    <Box className="picker-row">
                      <Tooltip title="Pick Gradient 1 Color">
                        <Box className="color-swatch-box" style={{ backgroundColor: parsedG1.hex }}>
                          <input
                            type="color"
                            className="hidden-color-input"
                            value={parsedG1.hex.startsWith("#") && parsedG1.hex.length === 7 ? parsedG1.hex : "#0A6836"}
                            onChange={handlePicker1Change}
                          />
                        </Box>
                      </Tooltip>
                      <Box className="color-text-field-wrap">
                        <TextField
                          label="Gradient 1"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={gradientInput1}
                          onChange={(e) => handleGradientInput1Change(e.target.value)}
                          error={!!gradientError1}
                          helperText={gradientError1 || "Hex or RGBA"}
                        />
                      </Box>
                    </Box>
                  </Box>

                  {/* Gradient 2: End Color */}
                  <Box className="gradient-col-card">
                    <Typography className="gradient-col-title">Gradient 2 (End)</Typography>
                    <Box className="picker-row">
                      <Tooltip title="Pick Gradient 2 Color">
                        <Box className="color-swatch-box" style={{ backgroundColor: parsedG2.hex }}>
                          <input
                            type="color"
                            className="hidden-color-input"
                            value={parsedG2.hex.startsWith("#") && parsedG2.hex.length === 7 ? parsedG2.hex : "#22c55e"}
                            onChange={handlePicker2Change}
                          />
                        </Box>
                      </Tooltip>
                      <Box className="color-text-field-wrap">
                        <TextField
                          label="Gradient 2"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={gradientInput2}
                          onChange={(e) => handleGradientInput2Change(e.target.value)}
                          error={!!gradientError2}
                          helperText={gradientError2 || "Hex or RGBA"}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Gradient Presets */}
                <Typography className="field-section-label">Gradient Presets</Typography>
                <Box className="gradient-presets-grid">
                  {PRESET_GRADIENTS.map((p) => {
                    const isActive =
                      parsedG1.hex.toLowerCase() === p.color1.toLowerCase() &&
                      parsedG2.hex.toLowerCase() === p.color2.toLowerCase();
                    return (
                      <Box
                        key={p.name}
                        className={`gradient-preset-item ${isActive ? "gradient-preset-active" : ""}`}
                        onClick={() => handleSelectGradientPreset(p.color1, p.color2)}
                      >
                        <Box
                          className="gradient-preset-pill"
                          style={{ background: `linear-gradient(135deg, ${p.color1} 0%, ${p.color2} 100%)` }}
                        >
                          {isActive && <Check size={13} color={getGradientContrastTextColor(p.color1, p.color2)} />}
                        </Box>
                        <Typography className="preset-swatch-name">{p.name}</Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* ===================== SECTION 3: THEME TEXT COLOR (UNIVERSAL) ===================== */}
            <Box className="text-color-customizer-section">
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5, gap: 1 }}>
                <Box>
                  <Typography className="field-section-label" sx={{ mb: "2px !important" }}>
                    Theme Text &amp; Contrast Color (Universal)
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#64748b", lineHeight: 1.4 }}>
                    Applies universally to <strong>ALL</strong> theme elements across the app: Single Color &amp; Gradient modes, all contained buttons, dashboard banners, action cards, active filter pills, badges, dialogs, and login screen.
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  className="text-color-action-btn"
                  startIcon={<Wand2 size={13} />}
                  onClick={() => {
                    const auto = isGradientMode
                      ? getGradientContrastTextColor(parsedG1.hex, parsedG2.hex)
                      : getContrastTextColor(currentHex);
                    setTextColor(auto);
                    setTextColorInput(auto);
                    setTextColorError("");
                  }}
                  sx={{ flexShrink: 0 }}
                >
                  Auto-Detect
                </Button>
              </Box>

              {/* Quick Preset Buttons: Light (#FFFFFF) vs Dark (#0F172A) */}
              <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
                <Button
                  variant={textColor?.toLowerCase() === "#ffffff" ? "contained" : "outlined"}
                  size="small"
                  onClick={() => {
                    setTextColor("#ffffff");
                    setTextColorInput("#ffffff");
                    setTextColorError("");
                  }}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: "8px",
                    backgroundColor: textColor?.toLowerCase() === "#ffffff" ? "#0f172a" : "#ffffff",
                    color: textColor?.toLowerCase() === "#ffffff" ? "#ffffff" : "#0f172a",
                    borderColor: "#cbd5e1",
                    "&:hover": {
                      backgroundColor: textColor?.toLowerCase() === "#ffffff" ? "#1e293b" : "#f1f5f9",
                    },
                  }}
                >
                  ⚪ Light Text (#FFFFFF)
                </Button>
                <Button
                  variant={textColor?.toLowerCase() === "#0f172a" ? "contained" : "outlined"}
                  size="small"
                  onClick={() => {
                    setTextColor("#0f172a");
                    setTextColorInput("#0f172a");
                    setTextColorError("");
                  }}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: "8px",
                    backgroundColor: textColor?.toLowerCase() === "#0f172a" ? "#0f172a" : "#ffffff",
                    color: textColor?.toLowerCase() === "#0f172a" ? "#ffffff" : "#0f172a",
                    borderColor: "#cbd5e1",
                    "&:hover": {
                      backgroundColor: textColor?.toLowerCase() === "#0f172a" ? "#1e293b" : "#f1f5f9",
                    },
                  }}
                >
                  ⚫ Dark Text (#0F172A)
                </Button>
              </Box>

              {/* Custom Text Color Swatch & Input */}
              <Box className="picker-row">
                <Tooltip title="Click to pick custom text color">
                  <Box className="color-swatch-box" style={{ backgroundColor: isValidColor(textColor) ? textColor : "#ffffff" }}>
                    <input
                      type="color"
                      className="hidden-color-input"
                      value={textColor?.startsWith("#") && textColor?.length === 7 ? textColor : "#ffffff"}
                      onChange={(e) => {
                        const hex = e.target.value;
                        setTextColor(hex);
                        setTextColorInput(hex);
                        setTextColorError("");
                      }}
                    />
                  </Box>
                </Tooltip>

                <Box className="color-text-field-wrap">
                  <TextField
                    label="Universal Theme Text Color (Hex or RGBA)"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={textColorInput}
                    onChange={(e) => {
                      const val = e.target.value;
                      setTextColorInput(val);
                      if (isValidColor(val)) {
                        const p = parseColor(val);
                        setTextColor(p.hex || val);
                        setTextColorError("");
                      } else {
                        setTextColorError("Please enter a valid Hex (e.g. #FFFFFF) or RGBA color");
                      }
                    }}
                    placeholder="#FFFFFF or #0F172A"
                    error={!!textColorError}
                    helperText={textColorError || "Applies universally everywhere the theme color/gradient is used"}
                    InputProps={{
                      endAdornment: isValidColor(textColorInput) ? (
                        <Check size={18} color="#10b981" />
                      ) : null,
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box className="panel-actions-row">
              <Button
                variant="outlined"
                className="reset-btn"
                startIcon={<RotateCcw size={16} />}
                onClick={handleReset}
              >
                Reset Default
              </Button>

              <Button
                variant="contained"
                className="save-settings-btn"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save size={16} />}
                onClick={handleSave}
                disabled={saving || (themeType === "solid" ? !!colorError : (!!gradientError1 || !!gradientError2))}
                style={{
                  background: isGradientMode ? activePreviewGradient : activePreviewColor,
                  backgroundColor: activePreviewColor,
                  color: contrastText,
                }}
              >
                {saving
                  ? "Saving Theme..."
                  : `Save ${themeType === "solid" ? "Single Color" : "Gradient"} Theme`}
              </Button>
            </Box>
          </Paper>

          {/* Right Column: Live Interactive Preview */}
          <Paper className="settings-panel live-preview-panel" elevation={1}>
            <Box className="panel-header">
              <Box className="panel-icon-wrap" style={{ backgroundColor: lightColor }}>
                <Sparkles size={20} style={{ color: activePreviewColor }} />
              </Box>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h6" className="panel-title-text">
                    Live Preview
                  </Typography>
                  <Chip
                    label={themeType === "solid" ? "Single Color Theme" : "Gradient Theme"}
                    size="small"
                    style={{
                      backgroundColor: lightColor,
                      color: activePreviewColor,
                      fontWeight: 700,
                      fontSize: "11px",
                      height: "22px",
                    }}
                  />
                </Box>
                <Typography className="panel-subtitle-text">
                  Real-time preview of how the {themeType === "solid" ? "single color" : "gradient"} theme looks across the app.
                </Typography>
              </Box>
            </Box>

            <Divider className="panel-divider" />

            <Box className="preview-showcase-container">
              {/* 1. Header Gradient / Solid Preview */}
              <Typography className="preview-label">
                Dashboard Welcome Banner ({themeType === "solid" ? "Single Color" : "Dual Gradient"})
              </Typography>
              <Box className="preview-gradient-banner" style={{ background: activePreviewGradient, color: contrastText }}>
                <Typography className="preview-banner-date" style={{ color: contrastSubText }}>Sunday, 20 Sep 2026</Typography>
                <Typography className="preview-banner-title" style={{ color: contrastText }}>
                  Welcome back, <span style={{ fontWeight: 800 }}>{employer?.employerName || "Employer"}</span>
                </Typography>
              </Box>

              {/* 2. Action Card Preview */}
              <Typography className="preview-label" style={{ marginTop: 20 }}>
                Metric Action Card ({themeType === "solid" ? "Single Color" : "Dual Gradient"})
              </Typography>
              <Box
                className="preview-action-card"
                style={{
                  background: activePreviewGradient,
                  borderRadius: "14px",
                  padding: "16px 20px",
                  color: contrastText,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  boxShadow: "0 4px 14px rgba(0,0,0,0.1)",
                }}
              >
                <Box>
                  <Typography style={{ fontSize: "12px", color: contrastSubText, opacity: 0.9, textTransform: "uppercase", fontWeight: 700 }}>
                    Quick Action
                  </Typography>
                  <Typography style={{ fontSize: "18px", color: contrastText, fontWeight: 800, marginTop: 2 }}>
                    + New Customer Entry
                  </Typography>
                </Box>
                <Box style={{ background: contrastOverlay, borderRadius: "10px", padding: "8px" }}>
                  <Sparkles size={20} color={contrastText} />
                </Box>
              </Box>

              {/* 3. Primary Buttons Preview */}
              <Typography className="preview-label" style={{ marginTop: 20 }}>
                Button &amp; Action States (Hover each to test)
              </Typography>
              <Box className="preview-buttons-row">
                <Button
                  variant="contained"
                  className="preview-interactive-btn preview-primary-btn"
                  style={{
                    background: isGradientMode ? activePreviewGradient : activePreviewColor,
                    backgroundColor: activePreviewColor,
                    color: contrastText,
                  }}
                >
                  Primary Action
                </Button>

                <Button
                  variant="contained"
                  className="preview-interactive-btn preview-hover-demo-btn"
                  style={{
                    background: isGradientMode ? activePreviewGradient : activePreviewColor,
                    backgroundColor: hoverColor,
                    color: contrastText,
                  }}
                >
                  Hover State
                </Button>

                <Button
                  variant="outlined"
                  className="preview-interactive-btn preview-outlined-btn"
                  style={{
                    borderColor: activePreviewColor,
                    color: activePreviewColor,
                  }}
                >
                  Outlined Action
                </Button>
              </Box>

              {/* 4. Badge & Active Filter Preview */}
              <Typography className="preview-label" style={{ marginTop: 20 }}>
                Active Filter &amp; Status Tags
              </Typography>
              <Box className="preview-tags-row">
                <Box className="preview-pill-active" style={{ backgroundColor: activePreviewColor, color: contrastText }}>
                  This Month
                </Box>
                <Box className="preview-tag-light" style={{ backgroundColor: lightColor, color: activePreviewColor }}>
                  Active Tenant: {employer?.prefix || "TENANT"}
                </Box>
              </Box>

              {/* 5. Login Form Preview */}
              <Typography className="preview-label" style={{ marginTop: 20 }}>
                Login Screen Card
              </Typography>
              <Box className="preview-login-card">
                <Box className="preview-input-sim" style={{ borderColor: activePreviewColor }}>
                  <Typography style={{ fontSize: "12px", color: activePreviewColor, fontWeight: 600 }}>
                    Mobile Number (Focused)
                  </Typography>
                  <Typography style={{ fontSize: "14px", color: "#0f172a", marginTop: 2 }}>
                    98765 43210
                  </Typography>
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  className="preview-login-submit-btn"
                  style={{
                    background: isGradientMode ? activePreviewGradient : activePreviewColor,
                    backgroundColor: activePreviewColor,
                    color: contrastText,
                    borderRadius: "12px",
                    height: "44px",
                    marginTop: "12px",
                    textTransform: "none",
                    fontWeight: 700,
                    boxShadow: `0 4px 14px ${toRgbaString(activePreviewColor, 0.35)}`,
                  }}
                >
                  Sign In to Portal
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}
